import React, { useState, useRef } from "react";
import axios from "axios";
import "./bulkupload.css";
import { McFileUpload } from "@maersk-global/mds-react-wrapper/components-core/mc-file-upload";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { McTable } from "@maersk-global/mds-react-wrapper/components-core/mc-table";
import { McTopBar } from "@maersk-global/mds-react-wrapper/components-core/mc-top-bar";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import { AL } from "../../al";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jwtDecode } from "jwt-decode";
import BulkApproval from "./BulkApproval";

const PO_ADMIN_EMAILS = (process.env.REACT_APP_PO_ADMIN_EMAILS || "")
    .split(",")
    .map(e => e.trim().toLowerCase())
    .filter(Boolean);

// ── Role helpers ──────────────────────────────────────────────────
function isPOAdmin(email) {
    return Boolean(email) && PO_ADMIN_EMAILS.includes(email.toLowerCase());
}

function hasPOAccess(roles) {
    if (!roles) return false;
    return roles.includes("IDAVestasApprover") || roles.includes("IDAMaerskCX") || roles.includes("IDAMaerskBilling");
}

function hasInvoiceAccess(roles) {
    if (!roles) return false;
    return roles.includes("IDAMaerskCX") || roles.includes("IDAMaerskBilling");
}

// ── Reusable upload panel ─────────────────────────────────────────
function UploadPanel({
    mode,           // "invoice" | "po"
    onUploadDone,   // (summary, results) => void
    showToast,
    token
}) {
    const [excelFile, setExcelFile]     = useState(null);
    const [uploading, setUploading]     = useState(false);
    const [progress, setProgress]       = useState(null);
    const excelFileRef                  = useRef(null);
    const BATCH_SIZE = 10;

    const handleFileChange = (e) => {
        const file = e?.detail?.[0];
        if (file) {
            excelFileRef.current = file;
            setExcelFile(file);
        }
    };

    const handleUpload = async () => {
        const fileToUpload = excelFileRef.current;
        if (!fileToUpload) {
            showToast("Please select an Excel file before uploading", "error");
            return;
        }

        const formData = new FormData();
        formData.append("file", fileToUpload);

        setUploading(true);
        setProgress(null);
        showToast("Uploading file...", "info");

        const accessToken = token || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
        if (!accessToken) {
            showToast("Authentication token not found", "error");
            setUploading(false);
            return;
        }

        const endpoint = mode === "invoice"
            ? `${process.env.REACT_APP_API_BASE_URL}/bulk-update-invoice?batch_size=${BATCH_SIZE}`
            : `${process.env.REACT_APP_API_BASE_URL}/bulk-update-po?batch_size=${BATCH_SIZE}`;

        try {
            const response = await fetch(endpoint, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "consumer-key": process.env.REACT_APP_FORGEROCK_CONSUMER_KEY
                },
                body: formData
            });

            // Non-OK → JSON error response (validation failures, missing columns, etc.)
            if (!response.ok) {
                let errorData;
                try { errorData = await response.json(); } catch { errorData = {}; }
                const errorMsg = errorData.error || errorData.message || `Upload failed (HTTP ${response.status})`;
                showToast(errorMsg, "error");
                if (errorData.results) {
                    onUploadDone(
                        { total_rows: errorData.total_rows || 0, valid_rows: 0, success_count: 0, error_count: errorData.results.length },
                        errorData.results
                    );
                }
                return;
            }

            // Check if response is SSE stream or plain JSON (backward compat)
            const contentType = response.headers.get("content-type") || "";

            if (contentType.includes("text/event-stream")) {
                // ── SSE stream handling ──
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = "";

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const parts = buffer.split("\n\n");
                    buffer = parts.pop(); // keep incomplete chunk

                    for (const part of parts) {
                        if (!part.trim()) continue;
                        let eventType = "", eventData = "";
                        for (const line of part.split("\n")) {
                            if (line.startsWith("event: ")) eventType = line.slice(7).trim();
                            else if (line.startsWith("data: ")) eventData = line.slice(6);
                        }
                        if (!eventData) continue;

                        try {
                            const parsed = JSON.parse(eventData);

                            if (eventType === "progress") {
                                setProgress({
                                    percentage:    parsed.percentage,
                                    processed:     parsed.processed,
                                    total:         parsed.total,
                                    success_count: parsed.success_count,
                                    error_count:   parsed.error_count
                                });
                            } else if (eventType === "complete") {
                                onUploadDone(
                                    { total_rows: parsed.total_rows, valid_rows: parsed.valid_rows, success_count: parsed.success_count, error_count: parsed.error_count },
                                    parsed.results || []
                                );
                                if (parsed.error_count === 0) {
                                    showToast(`Successfully updated ${parsed.success_count} record(s)!`, "success");
                                } else {
                                    showToast(`Completed: ${parsed.success_count} success, ${parsed.error_count} errors`, "warning");
                                }
                            } else if (eventType === "error") {
                                showToast(parsed.error || "An error occurred", "error");
                            }
                        } catch (e) {
                            console.error("SSE parse error:", e);
                        }
                    }
                }
            } else {
                // ── Fallback: plain JSON response ──
                const data = await response.json();
                if (data.status === "completed") {
                    onUploadDone(
                        { total_rows: data.total_rows, valid_rows: data.valid_rows, success_count: data.success_count, error_count: data.error_count },
                        data.results || []
                    );
                    if (data.error_count === 0) showToast(`Successfully updated ${data.success_count} record(s)!`, "success");
                    else showToast(`Completed: ${data.success_count} success, ${data.error_count} errors`, "warning");
                } else {
                    showToast(data.message || "Upload failed", "error");
                }
            }
        } catch (error) {
            showToast(error.message || "Upload failed", "error");
        } finally {
            setUploading(false);
            setProgress(null);
        }
    };

    const colLabel  = mode === "invoice" ? "Child ID + Invoice Number" : "Parent ID + Child ID + PO Number";
    const btnLabel  = uploading ? "Processing..." : "Upload & Process";

    return (
        <div className="bulk-upload-single">
            <div className="bulk-card">
                <h4 className="bulk-card-title">
                    {mode === "invoice" ? "Upload Invoice Excel File" : "Upload PO Excel File"}
                </h4>
                <p className="bulk-card-desc">
                    File must contain the required columns: <strong>{colLabel}</strong>
                </p>

                <div className="bulk-file-upload-area">
                    <McFileUpload
                        label="Select Excel File"
                        accept=".xlsx,.xls"
                        input={handleFileChange}
                    />
                </div>

                {excelFile && (
                    <div className="bulk-file-info">
                        <div className="bulk-file-info-row">
                            <span className="bulk-file-info-label">File</span>
                            <span className="bulk-file-info-value">{excelFile.name}</span>
                        </div>
                        <div className="bulk-file-info-row">
                            <span className="bulk-file-info-label">Size</span>
                            <span className="bulk-file-info-value">{(excelFile.size / 1024).toFixed(1)} KB</span>
                        </div>
                    </div>
                )}

                <div className="bulk-upload-action">
                    <McButton
                        onClick={handleUpload}
                        appearance="primary"
                        disabled={!excelFile || uploading}
                    >
                        {btnLabel}
                    </McButton>
                </div>

                {uploading && (
                    <div className="bulk-progress">
                        {progress ? (
                            <>
                                <div className="bulk-progress-header">
                                    <span className="bulk-progress-pct">{progress.percentage}%</span>
                                    <span className="bulk-progress-counts">
                                        {progress.processed} / {progress.total} records
                                    </span>
                                </div>
                                <div className="bulk-progress-bar-track">
                                    <div
                                        className="bulk-progress-bar-fill"
                                        style={{ width: `${progress.percentage}%` }}
                                    />
                                </div>
                                <div className="bulk-progress-stats">
                                    <span className="bulk-progress-stat-ok">✓ {progress.success_count} success</span>
                                    {progress.error_count > 0 && (
                                        <span className="bulk-progress-stat-err">✗ {progress.error_count} errors</span>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="bulk-progress-initial">
                                <div className="loader"></div>
                                <p className="bulk-progress-text">Validating and uploading file...</p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────
function BulkInvoiceUpload() {
    const navigate    = useNavigate();
    const { token, userInfo, roles } = useUser();

    // Derive email from token / context (kept for any future use)
    const email = (() => {
        try {
            const raw = token || localStorage.getItem(process.env.REACT_APP_CIAM_ACCESS_KEY);
            if (!raw) return "";
            return (jwtDecode(raw).email || "").toLowerCase();
        } catch { return ""; }
    })();

    const canInvoice = hasInvoiceAccess(roles);
    const canPO      = hasPOAccess(roles);
    const canApprove = isPOAdmin(email);

    // Default tab: whichever they have access to first
    const defaultTab = canInvoice ? "invoice" : canPO ? "po" : canApprove ? "approval" : "invoice";
    const [activeTab, setActiveTab] = useState(defaultTab);
    const activeTabRef = useRef(defaultTab);
    const setActiveTabSync = (tab) => { activeTabRef.current = tab; setActiveTab(tab); };

    // Per-tab state
    const [invoiceComplete, setInvoiceComplete] = useState(false);
    const [invoiceSummary,  setInvoiceSummary]  = useState(null);
    const [invoiceResults,  setInvoiceResults]  = useState([]);

    const [poComplete, setPoComplete] = useState(false);
    const [poSummary,  setPoSummary]  = useState(null);
    const [poResults,  setPoResults]  = useState([]);

    const [toastVisible,    setToastVisible]    = useState(false);
    const [toastMessage,    setToastMessage]    = useState("");
    const [toastAppearance, setToastAppearance] = useState("info");

    const showToast = (message, appearance = "info") => {
        setToastMessage(message);
        setToastAppearance(appearance);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 4000);
    };

    const handleLogout = () => {
        AL.logoutRedirect({ post_logout_uri: window.location.origin + "/" });
    };

    // ── Template download ─────────────────────────────────────────
    const downloadTemplate = (mode) => {
        const rows = mode === "invoice"
            ? [["Child ID", "Invoice Number"], ["C-01061", "INV2026001"], ["C-01062", "INVV2026002"]]
            : [["Parent ID", "Child ID", "PO Number"], ["CN-00100", "C-01061", "1234567890"], ["CN-00100", "C-01062", "9876543210"]];
        const ws  = XLSX.utils.aoa_to_sheet(rows);
        const wb  = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Template");
        const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([out], { type: "application/octet-stream" }),
            mode === "invoice" ? "bulk_invoice_template.xlsx" : "bulk_po_template.xlsx");
        showToast("Template downloaded", "success");
    };

    // ── Results download ──────────────────────────────────────────
    const downloadResults = (mode) => {
        const res = mode === "invoice" ? invoiceResults : poResults;
        if (!res.length) { showToast("No results to download", "warning"); return; }
        const exportData = mode === "invoice"
            ? res.map(r => ({ Row: r.row, "Child ID": r.child_id, "Invoice Number": r.invoice_number, Status: r.status, Message: r.message, "SF Record ID": r.sf_record_id || "" }))
            : res.map(r => ({ Row: r.row, "Parent ID": r.parent_id, "Child ID": r.child_id, "PO Number": r.po_number, Status: r.status, Message: r.message, "SF Record ID": r.sf_record_id || "" }));
        const ws  = XLSX.utils.json_to_sheet(exportData);
        const wb  = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Results");
        const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const ts  = new Date().toISOString().replace(/[:.]/g, "-");
        saveAs(new Blob([out], { type: "application/octet-stream" }), `bulk_${mode}_results_${ts}.xlsx`);
        showToast("Results downloaded", "success");
    };

    // ── Reset ─────────────────────────────────────────────────────
    const resetTab = (mode) => {
        if (mode === "invoice") {
            setInvoiceComplete(false); setInvoiceSummary(null); setInvoiceResults([]);
        } else if (mode === "po") {
            setPoComplete(false); setPoSummary(null); setPoResults([]);
        }
    };

    // ── Shared results panel ──────────────────────────────────────
    const ResultsPanel = ({ mode, summary, results }) => {
        const isPO = mode === "po";
        const columns = isPO
            ? [
                { id: "row",       label: "Row",      noWrap: true },
                { id: "parent_id", label: "Parent ID",  noWrap: true },
                { id: "child_id",  label: "Child ID",   noWrap: true },
                { id: "po_number", label: "PO Number",  noWrap: true },
                { id: "status",    label: "Status",     noWrap: true },
                { id: "message",   label: "Message",    width: "300px" }
              ]
            : [
                { id: "row",            label: "Row #",          noWrap: true },
                { id: "child_id",       label: "Child ID",       noWrap: true },
                { id: "invoice_number", label: "Invoice Number", noWrap: true },
                { id: "status",         label: "Status",         noWrap: true },
                { id: "message",        label: "Message",        width: "300px" }
              ];

        const tableData = isPO
            ? results.map((r, i) => ({ id: i, row: r.row, parent_id: r.parent_id, child_id: r.child_id, po_number: r.po_number, status: r.status, message: r.message }))
            : results.map((r, i) => ({ id: i, row: r.row, child_id: r.child_id, invoice_number: r.invoice_number, status: r.status, message: r.message }));

        return (
            <div>
                {/* Summary cards */}
                <div className="bulk-summary-grid">
                    <div className="bulk-summary-card bulk-summary-total">
                        <div className="bulk-summary-value">{summary.total_rows}</div>
                        <div className="bulk-summary-label">Total Rows</div>
                    </div>
                    <div className="bulk-summary-card bulk-summary-valid">
                        <div className="bulk-summary-value">{summary.valid_rows}</div>
                        <div className="bulk-summary-label">Valid Rows</div>
                    </div>
                    <div className="bulk-summary-card bulk-summary-success">
                        <div className="bulk-summary-value">{summary.success_count}</div>
                        <div className="bulk-summary-label">Successful</div>
                    </div>
                    <div className="bulk-summary-card bulk-summary-error">
                        <div className="bulk-summary-value">{summary.error_count}</div>
                        <div className="bulk-summary-label">Errors</div>
                    </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: "flex", gap: "12px", marginBottom: "20px", justifyContent: "flex-end" }}>
                    <McButton onClick={() => downloadResults(mode)} label="Download Results" appearance="secondary" fit="small" />
                    <McButton onClick={() => resetTab(mode)}        label="New Upload"       appearance="primary"   fit="small" />
                </div>

                {/* Detailed table */}
                <div className="bulk-table-wrapper">
                    <h4 className="bulk-card-title" style={{ marginBottom: "16px" }}>Detailed Results</h4>
                    <div style={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        overflow: "hidden",
                        height: results.length > 10 ? "480px" : "auto",
                        display: "flex",
                        flexDirection: "column"
                    }}>
                        <div style={{ flex: 1, overflowY: results.length > 10 ? "auto" : "visible", minHeight: 0 }}>
                            <McTable
                                data={tableData}
                                datakey="id"
                                columns={columns.map(col => ({
                                    ...col,
                                    cellTemplate: col.id === "status"
                                        ? (e) => {
                                            const val = e.rowData.status || "";
                                            const ok  = val.includes("success");
                                            const appearance = ok ? "success" : "error";
                                            const label = ok ? "Success" : "Error";
                                            return e.html`<mc-tag label="${label}" appearance="${appearance}"></mc-tag>`;
                                        }
                                        : undefined
                                }))}
                                zebrastripes
                                headersticky
                                fit="small"
                                height="auto"
                                recordsperpage={tableData.length || 25}
                                style={{ border: "none" }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="mds">
            {/* ── Top Bar ── */}
            <McTopBar
                product="Maersk | Unplanned Charges"
                productshort="Maersk"
                onClick={() => navigate("/")}
                style={{ paddingLeft: "20px", height: "64px", paddingRight: "20px", marginTop: "-5px", borderBottom: "1px solid #DBDBDB" }}
            >
                <div slot="actions" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <McButton onClick={() => navigate("/")} label="Go to Home" appearance="primary"   fit="small" />
                    <McButton onClick={handleLogout}        label="Sign Out"   appearance="secondary" fit="small" />
                </div>
            </McTopBar>

            {/* ── Main ── */}
            <main className="mds-page mds-container" style={{ padding: "2rem", minHeight: "95vh", backgroundColor: "#f8f9fa" }}>

                {/* Page header */}
                <div className="bulk-page-header">
                    <div>
                        <h3 className="main-heading">Bulk Upload</h3>
                        <p className="bulk-page-subtitle">
                            {activeTab === "invoice"
                                ? "Update invoice numbers for multiple charge records at once."
                                : activeTab === "po"
                                ? "Assign PO numbers to multiple charge records at once."
                                : activeTab === "approval"
                                ? "Approve or reject event and cost charges for multiple records at once."
                                : "Update invoice numbers for multiple charge records at once."}
                        </p>
                    </div>
                    {activeTab !== "approval" && (
                        <McButton
                            onClick={() => downloadTemplate(activeTabRef.current)}
                            label="Download Template"
                            appearance="secondary"
                            fit="small"
                        />
                    )}
                </div>

                {/* ── Tabs ── */}
                {(canInvoice || canPO || canApprove) && (
                    <div className="bulk-tabs">
                        {canInvoice && (
                            <button
                                className={`bulk-tab${activeTab === "invoice" ? " bulk-tab--active" : ""}`}
                                onClick={() => setActiveTabSync("invoice")}
                            >
                                Invoice Upload
                            </button>
                        )}
                        {canPO && (
                            <button
                                className={`bulk-tab${activeTab === "po" ? " bulk-tab--active" : ""}`}
                                onClick={() => setActiveTabSync("po")}
                            >
                                PO Upload
                            </button>
                        )}
                        {canApprove && (
                            <button
                                className={`bulk-tab${activeTab === "approval" ? " bulk-tab--active" : ""}`}
                                onClick={() => setActiveTabSync("approval")}
                            >
                                Approval / Rejection
                            </button>
                        )}
                    </div>
                )}

                {/* ── Invoice tab ── */}
                {activeTab === "invoice" && canInvoice && (
                    invoiceComplete && invoiceSummary
                        ? <ResultsPanel mode="invoice" summary={invoiceSummary} results={invoiceResults} />
                        : <UploadPanel
                            mode="invoice"
                            token={token}
                            showToast={showToast}
                            onUploadDone={(summary, results) => {
                                setInvoiceSummary(summary);
                                setInvoiceResults(results);
                                setInvoiceComplete(true);
                            }}
                          />
                )}

                {/* ── PO tab ── */}
                {activeTab === "po" && canPO && (
                    poComplete && poSummary
                        ? <ResultsPanel mode="po" summary={poSummary} results={poResults} />
                        : <UploadPanel
                            mode="po"
                            token={token}
                            showToast={showToast}
                            onUploadDone={(summary, results) => {
                                setPoSummary(summary);
                                setPoResults(results);
                                setPoComplete(true);
                            }}
                          />
                )}

                {/* ── Approval / Rejection tab (admin only) ── */}
                {activeTab === "approval" && canApprove && (
                    <BulkApproval token={token} showToast={showToast} />
                )}

                {/* No access fallback */}
                {!canInvoice && !canPO && !canApprove && (
                    <div className="bulk-no-access">
                        <p>You do not have permission to access this page.</p>
                    </div>
                )}

            </main>

            <McToast appearance={toastAppearance} open={toastVisible} close={() => setToastVisible(false)}>
                <McNotification body={toastMessage} />
            </McToast>
        </div>
    );
}

export default BulkInvoiceUpload;
